import {
  Button,
  Col,
  DatePicker,
  Form,
  Input,
  Layout,
  message,
  Radio,
  Row,
  Select,
  Upload,
} from "antd";
import { useWatch } from "antd/es/form/Form";
import { Trash, Plus, Inbox, ExternalLink } from "lucide-react";
import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import Navbar from "../../Components/Navbar/Navbar";
import PageHeader from "../../Components/PageHeader/PageHeader";
import ViewAttachmentModal from "../../Components/CustomModals/ViewAttachmentModal";
import ErrorModal from "../../Components/CustomModals/ErrorModal";
import SuccessModal from "../../Components/CustomModals/SuccessModal";
import ReasonModal from "../../Components/CustomModals/ReasonModal";
import {
  formatNumbers,
  formatString,
  navigateTo,
} from "../../Utils/Common";
import { getRequest, postRequest } from "../../Utils/ApiCalls";
import dayjs from "dayjs";
import PersistentFooter from "../../Components/PersistentFooter/PersistentFooter";
import { useParams } from "react-router-dom";

const { Dragger } = Upload;
const { Option } = Select;

interface PettyCashOutFormProps {
  add?: boolean;
  edit?: boolean;
  view?: boolean;
}

const PettyCashOutForm: React.FC<PettyCashOutFormProps> = ({ add, edit, view }) => {
  const [loading, setLoading] = useState("");
  const [form] = Form.useForm();
  const [expenseTypes, setExpenseTypes]: any = useState([]);
  const [branches, setBranches]: any = useState({});
  const [status, setStatus] = useState("");
  const [withholdingTaxPercent, setWithholdingTaxPercent] = useState(0);
  const itemsArray = useWatch("itemsArray", form);
  const vatType = useWatch("vat_type", form);
  const { id: petty_cash_id } = useParams<{ id: string }>();
  const [showAttachment, setShowAttachment] = useState(false);
  const [attachmentUrl, setAttachmentUrl] = useState("");
  const [errorMessage,         setErrorMessage]      = useState("");
  const [successMessage,       setSuccessMessage]    = useState("");
  const [showDisapproveModal, setShowDisapproveModal] = useState(false);
  const [disapproveRemarks,   setDisapproveRemarks]   = useState("");

  // read-only when in view mode
  const isReadOnly = !!view;

  function clearFields() {
    form.resetFields();
  }

  const subTotal = (itemsArray || []).reduce((sum: number, item: any) => {
    return sum + (Number(item?.qty) || 0) * (Number(item?.price) || 0);
  }, 0);

  const vatAmount = (() => {
    const gross = subTotal;
    return gross - gross / 1.12;
  })();

  const netVat = subTotal / 1.12;

  const withholdingTaxAmount = subTotal * (withholdingTaxPercent / 100);
  const grandTotal = subTotal - withholdingTaxAmount;

  const handleBeforeUpload = (file: any) => {
    const isLt2M = file.size / 1024 / 1024 < 2;
    if (!isLt2M) {
      message.error(`${file.name} exceeds the 2MB size limit.`);
    }
    return isLt2M ? false : Upload.LIST_IGNORE;
  };

  const handleUploadChange = (info: any) => {
    form.setFieldValue("attachments", info.fileList);
  };

  async function submitForm(data: any) {
    const formattedItemsArray = (data.itemsArray || []).map((item: any) => ({
      ...item,
      amount: parseFloat(
        ((Number(item.qty) || 0) * (Number(item.price) || 0)).toFixed(2)
      ),
    }));

    const formattedData = {
      ...data,
      date:           data.date ? dayjs(data.date).format("YYYY-MM-DD") : "",
      wht_percentage: withholdingTaxPercent,
      wht_amount:     withholdingTaxAmount,
      grand_total:    grandTotal,
      vat_type:       vatType,
      itemsArray:     formattedItemsArray,
    };

    const formData = new FormData();

    for (const key in formattedData) {
      const value = formattedData[key];

      if (key === "itemsArray") {
        (formattedData.itemsArray || []).forEach((item: any) => {
          formData.append("item_names[]",       String(item.item_name ?? ""));
          formData.append("expense_type_ids[]", String(item.expense_type_id ?? ""));
          formData.append("qtys[]",             String(item.qty ?? ""));
          formData.append("units[]",            String(item.unit ?? ""));
          formData.append("prices[]",           String(item.price ?? ""));
          formData.append("amounts[]",          String(item.amount ?? ""));
        });
      } else if (key === "attachment" && Array.isArray(value) && value.length > 0) {
        // ✅ FIXED: Changed "attachments" to "attachment" to match form field name
        value.forEach((file: any) => {
          if (file.originFileObj) {
            formData.append("attachment", file.originFileObj);
          }
        });
      } else if (value !== undefined && value !== null && key !== "attachments") {
        formData.append(key, String(value));
      }
    }

    setLoading("submit");
    const endpoint = edit ? `pettycash/update` : `pettycash/create`;
    const response = await postRequest(endpoint, formData, true);

    if (response.success) {
      message.success(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-out");
      }, 1000);
    } else {
      setLoading("");
      message.error(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          `Failed to ${edit ? "update" : "create"} petty cash.`
      );
    }
  }

  async function submitApprove() {
    setLoading("approve");
    const response = await postRequest(
      `pettycash/change_status`,
      { petty_cash_id, action: "approved" },
      true
    );

    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-out");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to approve petty cash."
      );
    }
  }

  async function submitDisapprove() {
    if (!disapproveRemarks.trim()) {
      setErrorMessage("Please provide a reason for disapproval.");
      return;
    }
    setShowDisapproveModal(false);
    setLoading("disapprove");
    const response = await postRequest(
      `pettycash/change_status`,
      { petty_cash_id, action: "disapproved", reason: disapproveRemarks },
      true
    );

    if (response.success) {
      setSuccessMessage(formatString(response.data.message));
      setTimeout(() => {
        clearFields();
        setLoading("");
        navigateTo("/petty-cash-out");
      }, 1000);
    } else {
      setLoading("");
      setErrorMessage(
        response?.data?.response?.data?.messages?.error_messages?.[0] ??
          "Failed to disapprove petty cash."
      );
    }
  }

  async function fetchBranches() {
    setBranches({});
    setLoading("fetch");
    const response = await getRequest(`branches/get`, true);
    if (response.success) {
      setBranches(response.data.data[0]);
      form.setFieldsValue({ branch_id: response.data.data[0]?.id });
    }
    setLoading("");
  }

  async function fetchExpenseTypes() {
    setExpenseTypes([]);
    setLoading("fetch");
    const response = await getRequest(`expense_types/get`, true);
    if (response.success) {
      setExpenseTypes(response.data.data);
    }
    setLoading("");
  }

  // Shared fetch for edit AND view modes
  useEffect(() => {
    if ((edit || view) && petty_cash_id) {
      setLoading("fetch");
      getRequest(`pettycash/get`, { petty_cash_id }, true).then((response) => {
        if (response.success) {
          const data = response.data.data[0];
          setStatus(data.status);
          setWithholdingTaxPercent(Number(data.wht_percentage) || 0);

          // Populate attachment URL for view mode
          if (data.attachment_url) {
            setAttachmentUrl(data.attachment_url);
          }

          const items = (data.items || []).map((row: any) => ({
            item_name:       row.item_name,
            expense_type_id: row.expense_type_id,
            qty:             row.qty,
            unit:            row.unit,
            price:           row.price,
          }));

          form.setFieldsValue({
            branch_id:     data.branch_id,
            date:          data.date ? dayjs(data.date) : null,
            pc_no:         data.pc_no,
            supplier_name: data.supplier_name,
            requested_by:  data.requested_by,
            vat_type:      data.vat_type,
            source_from:   data.source_from,
            remarks:       data.remarks,
            itemsArray:    items,
          });
        }
        setLoading("");
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchExpenseTypes();
    fetchBranches();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Page title
  const pageTitle = add
    ? "Add Petty Cash Out Transaction"
    : edit
    ? "Edit Petty Cash Out Transaction"
    : "View Petty Cash Out Transaction";

  return (
    <Layout className="page">
      <Helmet>
        <title>Agua Lab ERP System</title>
      </Helmet>
      <Navbar />
      <Layout>
        <Row className="px-3 pt-20 md:px-10 md:pb-10 md:pt-8 w-screen md:w-full pb-[50px] sm:pb-0 overflow-scroll text-textColor">
          <Col xs={24}>
            <PageHeader
              title={pageTitle}
              withDateRangeFilter={false}
              withAddButton={false}
              withSearchBtn={false}
            />
          </Col>
          <div className="form-container w-full">
            <Col span={24}>
              <Form form={form} onFinish={submitForm} layout="vertical">
                <Row className="w-full" gutter={[10, 10]}>

                  {/* Branch - always read-only */}
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Branch"
                      name="branch_id"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <div
                        style={{
                          padding:      '4px 11px',
                          background:   '#f5f5f5',
                          border:       '1px solid #d9d9d9',
                          borderRadius: '6px',
                          color:        'rgba(0,0,0,0.88)',
                          minHeight:    '32px',
                        }}
                      >
                        {branches?.name}
                      </div>
                    </Form.Item>
                  </Col>

                  {/* Cash Out Date */}
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Cash Out Date"
                      name="date"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <DatePicker className="w-full" format={"MMMM D, YYYY"} disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* PC No - always read-only */}
                  <Col xs={24} lg={8}>
                    <Form.Item label="Petty Cash Number" name="pc_no">
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* Supplier Name */}
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Supplier Name (Payee)"
                      name="supplier_name"
                      rules={[{ required: !isReadOnly, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* Requested By */}
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Requested By"
                      name="requested_by"
                      rules={[{ required: !isReadOnly, whitespace: true, message: "This field is required." }]}
                    >
                      <Input disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* Vat Type */}
                  <Col xs={24} lg={8}>
                    <Form.Item
                      label="Vat Type"
                      name="vat_type"
                      rules={[{ required: !isReadOnly, message: "This field is required." }]}
                    >
                      <Select
                        placeholder="Select Vat Type"
                        showSearch
                        allowClear
                        disabled={isReadOnly}
                        filterOption={(input: string, option?: any) =>
                          option?.children?.toLowerCase().includes(input.toLowerCase())
                        }
                      >
                        <Option value="vat-in">Vat In</Option>
                        <Option value="vat-ex">Vat Ex</Option>
                        <Option value="zero-rated">Zero-Rated</Option>
                      </Select>
                    </Form.Item>
                  </Col>

                  {/* Remarks */}
                  <Col xs={24}>
                    <Form.Item label="Remarks" name="remarks">
                      <Input.TextArea disabled={isReadOnly} />
                    </Form.Item>
                  </Col>

                  {/* Items Table */}
                  <Row>
                    <span className="edit-label mb-2">
                      {!isReadOnly && <label className="text-red-500 me-1">*</label>}
                      List of Particulars:
                    </span>
                    <Form.List name="itemsArray" initialValue={[{}]}>
                      {(fields, { add, remove }) => (
                        <>
                          <Col xs={24}>
                            <div className="overflow-auto max-h-[600px]">
                              <table className="w-full table-fixed border border-collapse min-w-[800px]">
                                <thead>
                                  <tr className="bg-primary text-white font-bold border">
                                    <th className="p-2 border w-[100px]">Item Name</th>
                                    <th className="p-2 border w-[80px]">Expense Type</th>
                                    <th className="p-2 border w-[50px]">Qty</th>
                                    <th className="p-2 border w-[60px]">Unit</th>
                                    <th className="p-2 border w-[60px]">Price</th>
                                    <th className="p-2 border w-[60px]">Total</th>
                                    {!isReadOnly && (
                                      <th className="p-2 border w-[40px] text-center">Action</th>
                                    )}
                                  </tr>
                                </thead>
                                <tbody>
                                  {fields.map((field) => (
                                    <tr key={field.key} className="border bg-white">
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "item_name"]}
                                          rules={[{ required: !isReadOnly, message: "Required" }]}
                                          noStyle
                                        >
                                          <Input disabled={isReadOnly} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "expense_type_id"]}
                                          rules={[{ required: !isReadOnly, message: "Required" }]}
                                          noStyle
                                        >
                                          <Select
                                            className="w-full"
                                            placeholder="Select expense type"
                                            showSearch
                                            allowClear
                                            disabled={isReadOnly}
                                            filterOption={(input: string, option?: any) =>
                                              option?.children?.toLowerCase().includes(input.toLowerCase())
                                            }
                                          >
                                            {expenseTypes.map((type: any) => (
                                              <Select.Option key={type.id} value={type.id}>
                                                {type.name}
                                              </Select.Option>
                                            ))}
                                          </Select>
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "qty"]}
                                          rules={[{ required: !isReadOnly, message: "Required" }]}
                                          noStyle
                                        >
                                          <Input
                                            type="number"
                                            min={1}
                                            disabled={isReadOnly}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "unit"]}
                                          rules={[{ required: !isReadOnly, message: "Required" }]}
                                          noStyle
                                        >
                                          <Input disabled={isReadOnly} />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item
                                          name={[field.name, "price"]}
                                          rules={[{ required: !isReadOnly, message: "Required" }]}
                                          noStyle
                                        >
                                          <Input
                                            type="number"
                                            min={0}
                                            step="0.01"
                                            disabled={isReadOnly}
                                            onWheel={(e) => (e.target as HTMLInputElement).blur()}
                                          />
                                        </Form.Item>
                                      </td>
                                      <td className="p-2 border">
                                        <Form.Item shouldUpdate noStyle>
                                          {() => {
                                            const qty   = form.getFieldValue(["itemsArray", field.name, "qty"]);
                                            const price = form.getFieldValue(["itemsArray", field.name, "price"]);
                                            const total = (Number(qty) || 0) * (Number(price) || 0);
                                            return <Input value={total.toFixed(2)} disabled />;
                                          }}
                                        </Form.Item>
                                      </td>
                                      {!isReadOnly && (
                                        <td className="p-2 border text-center">
                                          {fields.length !== 1 && (
                                            <Button
                                              onClick={() => remove(field.name)}
                                              icon={<Trash size={17} />}
                                              danger
                                              type="text"
                                            />
                                          )}
                                        </td>
                                      )}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </Col>
                          <Col xs={24}>
                            {!isReadOnly && (
                              <Row>
                                <Col span={17} className="mt-2">
                                  <Button type="primary" onClick={() => add()} icon={<Plus size={16} />}>
                                    Add Item
                                  </Button>
                                </Col>
                              </Row>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Subtotal:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {subTotal.toFixed(2)}</Col>
                            </Row>
                            {vatType === "vat-in" && (
                              <>
                                <Row className="mt-2">
                                  <Col span={19} className="text-end"><strong>12% Vat:</strong></Col>
                                  <Col span={5} className="text-start ps-5">₱ {vatAmount.toFixed(2)}</Col>
                                </Row>
                                <Row className="mt-2">
                                  <Col span={19} className="text-end"><strong>Net of Vat:</strong></Col>
                                  <Col span={5} className="text-start ps-5">₱ {netVat.toFixed(2)}</Col>
                                </Row>
                              </>
                            )}
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-1">
                                {isReadOnly ? (
                                  <span className="ps-4">{withholdingTaxPercent}%</span>
                                ) : (
                                  <Radio.Group
                                    value={withholdingTaxPercent}
                                    onChange={(e) => setWithholdingTaxPercent(e.target.value)}
                                  >
                                    <Radio value={0}>0%</Radio>
                                    <Radio value={1}>1%</Radio>
                                    <Radio value={2}>2%</Radio>
                                  </Radio.Group>
                                )}
                              </Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Calculated Withholding Tax:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {withholdingTaxAmount.toFixed(2)}</Col>
                            </Row>
                            <Row className="mt-2">
                              <Col span={19} className="text-end"><strong>Grand Total:</strong></Col>
                              <Col span={5} className="text-start ps-5">₱ {formatNumbers(grandTotal)}</Col>
                            </Row>
                          </Col>
                        </>
                      )}
                    </Form.List>
                  </Row>

                  {/* Attachments — only shown on add or edit */}
                  {(edit || add) && (
                    <Col xs={24} className="pt-1">
                      <span className="edit-label mb-2">Deposit Slip:</span>
                      <Form.Item
                        name="attachment"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => e?.fileList || []}
                        rules={[{ required: !!add, message: "Deposit slip is required." }]}
                      >
                        <Dragger name="file" beforeUpload={handleBeforeUpload} onChange={handleUploadChange} accept=".pdf,image/*">
                          <p className="ant-upload-drag-icon flex justify-center items-center text-3xl"><Inbox /></p>
                          <p className="ant-upload-text">Click or drag file to upload</p>
                          <p className="ant-upload-hint">Supports PDF, Image formats.</p>
                        </Dragger>
                      </Form.Item>
                      <p className="text-textColor pt-2">Note: Maximum file size is 2MB per file.</p>
                    </Col>
                  )}

                  {/* View attachment button — only shown on view mode when a URL exists */}
                  {view && attachmentUrl && (
                    <Col xs={24} className="pt-1 mt-4">
                      <span className="font-bold block mb-2">Deposit Slip Attachment</span>
                      <Button
                        icon={<ExternalLink size={14} />}
                        onClick={() => setShowAttachment(true)}
                      >
                        View Attachment
                      </Button>
                    </Col>
                  )}

                  {/* Footer Buttons */}
                  <Col lg={24} className="mt-5 flex justify-end gap-3">
                    <Form.Item>
                      <Button
                        type="primary"
                        onClick={() => {
                          navigateTo("/petty-cash-out");
                          clearFields();
                        }}
                      >
                        Back
                      </Button>
                    </Form.Item>

                    {/* Approve button — only on view mode when status is pending */}
                    {view && status === "pending" && (
                      <>
                        <Form.Item>
                          <Button
                          type="primary"
                          loading={loading === "disapprove"}
                          onClick={() => { setDisapproveRemarks(""); setShowDisapproveModal(true); }}
                          danger
                        >
                          Disapprove
                        </Button>
                        </Form.Item>
                        <Form.Item>
                          <Button
                            type="primary"
                            loading={loading === "approve"}
                            onClick={submitApprove}
                          >
                            Approve
                          </Button>
                        </Form.Item>
                      </>
                    )}

                    {/* Submit / Update — only on add or edit mode */}
                    {!view && (
                      <Form.Item>
                        {edit ? (
                          <Button
                            htmlType="submit"
                            type="primary"
                            loading={loading === "submit"}
                          >
                            Update
                          </Button>
                        ) : (
                          <Button
                            htmlType="submit"
                            type="primary"
                            loading={loading === "submit"}
                          >
                            Submit
                          </Button>
                        )}
                      </Form.Item>
                    )}
                  </Col>
                </Row>
              </Form>
            </Col>
          </div>
        </Row>
      </Layout>
      <PersistentFooter />

      <ViewAttachmentModal
        showModal={showAttachment}
        attachmentUrl={attachmentUrl}
        onCancel={() => setShowAttachment(false)}
      />

      <ErrorModal
        showModal={!!errorMessage}
        onCancel={() => setErrorMessage("")}
        title="Action Failed"
        body={errorMessage}
      />

      <SuccessModal
        showModal={!!successMessage}
        onCancel={() => setSuccessMessage("")}
        title="Success"
        body={successMessage}
      />

      <ReasonModal
        showModal={showDisapproveModal}
        onCancel={() => setShowDisapproveModal(false)}
        title="Please provide a reason for disapproval:"
        reasonValue={disapproveRemarks}
        onReasonChange={(e: any) => setDisapproveRemarks(e.target.value)}
        footer={[
          <Button key="cancel" onClick={() => setShowDisapproveModal(false)}>
            Cancel
          </Button>,
          <Button
            key="confirm"
            type="primary"
            danger
            loading={loading === "disapprove"}
            onClick={submitDisapprove}
          >
            Confirm Disapprove
          </Button>,
        ]}
      />
    </Layout>
  );
};

export default PettyCashOutForm;